import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DEFAULT_PAGE } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { BehaviorSubject, ReplaySubject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Organization {
  id: number;
  name: string;
  subdomain: string;
  website: string;
  legalMentionUrl: string;
  logoFile: string;
  relationType: string;
  settings: any[];
}

const API_SUBDOMAIN = '/organizations/subdomains/:subdomain';
@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  private notFoundPage = '/error-domain';
  private validPaths = [this.notFoundPage];

  private organization: Organization;

  // oauth config loaded
  private isDoneLoadingSubject = new ReplaySubject<boolean>();
  public isDoneLoading = this.isDoneLoadingSubject.asObservable();

  // authentication done
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();

  private authConfig: AuthConfig;

  constructor(
    private location: Location,
    private router: Router,
    private httpApi: HttpApiService,
    private oauthService: OAuthService,
  ) {
    this.oauthEventListener();
  }

  async initialize(): Promise<void> {
    if (this.isValidPath()) return;
    const subdomain = this.extractSubdomainFromHostname();

    if (!subdomain) return;
    this.organization = await this.getOrganizationBySubdomain(subdomain);

    if (!this.organization) {
      this.navigateToNotFoundPage();
      return;
    }
    await this.configureOAuth(this.organization);
    await this.tryOAuthLogin();
  }

  private isValidPath(): boolean {
    return this.validPaths.includes(this.location.path(true));
  }

  private extractSubdomainFromHostname(): string {
    const hostnames = window.location.hostname.split('.');
    if (hostnames.length < 2) {
      this.navigateToNotFoundPage();
      return '';
    }
    return hostnames[0];
  }

  private navigateToNotFoundPage(): void {
    this.router.navigate([this.notFoundPage]);
  }

  private async getOrganizationBySubdomain(
    subdomain: string,
  ): Promise<any | null> {
    const url = API_SUBDOMAIN.replace(':subdomain', subdomain);
    try {
      const organization = await firstValueFrom(this.httpApi.get(url));
      return organization;
    } catch (error) {
      console.error('Failed to retrieve organization:', error);
      return null;
    }
  }

  private async configureOAuth(subdomain: Organization) {
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    this.authConfig = this.infereAuthConfig(subdomain);
    this.oauthService.configure(this.authConfig);
    await this.oauthService.loadDiscoveryDocument(
      environment.servers.authorization.url +
        '/.well-known/oauth-authorization-server',
    );
    this.isDoneLoadingSubject.next(true);
  }

  private async tryOAuthLogin() {
    try {
      await this.oauthService.tryLogin();
    } catch (e) {}
    if (this.oauthService.hasValidAccessToken()) {
      // Rafraîchit l'access token avant son expiration plutôt que de laisser l'utilisateur
      // tomber sur un 401. En flux `code` sans silent refresh, la librairie utilise bien le
      // grant refresh_token, déjà accordé au client espace-client.
      this.oauthService.setupAutomaticSilentRefresh();

      if (
        this.oauthService.state &&
        this.oauthService.state !== 'undefined' &&
        this.oauthService.state !== 'null'
      ) {
        let stateUrl = this.oauthService.state;
        if (!stateUrl.startsWith('/')) {
          stateUrl = decodeURIComponent(stateUrl);
        }
        console.warn(
          `There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`,
        );
        this.router.navigateByUrl(stateUrl);
      }
    }
  }

  getOrganization(): Organization {
    return this.organization;
  }

  /**
   * Nom affiché de l'organisation, « albR » tant qu'aucune organisation n'est chargée.
   */
  getOrganizationName(): string {
    return this.getOrganization()?.name || 'albR';
  }

  infereDomain(url: string, domain: string) {
    return url.replace('*', domain);
  }

  private infereAuthConfig(organization: Organization): AuthConfig {
    const config: AuthConfig = {
      responseType: 'code',
      issuer: environment.servers.authorization.url,
      redirectUri: this.infereDomain(
        environment.servers.client.redirect,
        organization.subdomain,
      ),
      clientId: environment.servers.authorization.client,
      scope: environment.servers.authorization.scope,
      showDebugInformation: true,
      requireHttps: false,
      customQueryParams: {
        organization_id: organization.id,
        organization_subdomain: organization.subdomain,
      },
    };
    return config;
  }

  private oauthEventListener() {
    this.oauthService.events.subscribe(event => {
      if (environment.servers.authorization.debug) {
        if (event instanceof OAuthErrorEvent)
          console.error('OAuthErrorEvent Object:', event);
        else console.warn('OAuthEvent Object:', event);

        if (event.type === 'token_expires') console.warn('token_expires');
      }

      // La librairie avale l'échec du rafraîchissement automatique dans un catch silencieux.
      // Sans ce traitement, une session révoquée resterait ouverte avec un token mort jusqu'au
      // premier appel d'API, sans que l'utilisateur comprenne pourquoi plus rien ne répond.
      if (event.type === 'token_refresh_error') {
        console.warn('Rafraîchissement impossible, la session est terminée');
        this.oauthService.logOut(true);
        this.oauthService.initLoginFlow();
        return;
      }

      if (event.type === 'invalid_nonce_in_state')
        this.router.navigate([DEFAULT_PAGE]);

      this.isAuthenticatedSubject.next(this.oauthService.hasValidAccessToken());
    });
  }
}
